// Tela do menu "Financeiro" (permissão de empresa "ATIVAR MODO FINANCEIRO").
//
// Primeira entrega (TODO_FINANCEIRO, Fase 1 / Camada 1): SOMENTE LEITURA.
// Mostra as OS com o valor cobrável composto (materiais consumidos + mão de obra)
// para os números serem validados ANTES de existir documento financeiro no banco.
// Não cria cobrança, não grava nada, não depende de tabela nova.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import FinanceiroCobrancas from './FinanceiroCobrancas';
import { mascaraMoedaBR, moedaParaNumero } from '../lib/moeda';

interface LinhaFinanceiro {
  id: number;
  codigo: string;
  status: string;
  tipo_os: string | null;
  referencia_periodo: string | null;
  tempo_total_minutos: number | null;
  cliente_nome: string | null;
  cliente_documento: string | null;
  tecnico_nome: string | null;
  solicitante_nome: string | null;
  unidade_nome: string | null;
  local_nome: string | null;
  ativo_nome: string | null;
  tecnico_valor_hora: number | null;
  mao_obra_valor: number;
  materiais_total: number | null;
  total_composto: number;
  orcamento_valor: number | null;
  mao_obra_incompleta: boolean;
}

interface RespostaFinanceiro {
  linhas: LinhaFinanceiro[];
  totais: {
    materiais: number | null;
    mao_obra: number;
    composto: number;
    orcamento_referencia: number;
    os_com_mao_obra_incompleta: number;
  };
  paginacao: { pagina: number; limite: number; total_registros: number; total_paginas: number };
  contexto: { base_periodo: string; status: string; pode_ver_materiais: boolean; somente_leitura: boolean };
}

const num = (v: unknown): number => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const fmtMoeda = (v: unknown): string => num(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtMinutos(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '-';
  const min = Math.max(0, Math.round(v));
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

function fmtData(v: string | null): string {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('pt-BR');
}

const REGEX_DIACRITICOS = new RegExp('[\u0300-\u036f]', 'g');

function normalizar(valor: string): string {
  return valor.normalize('NFD').replace(REGEX_DIACRITICOS, '').toLowerCase().trim();
}

function cardResumo(titulo: string, valor: string, detalhe: string) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ color: 'var(--texto-suave)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {titulo}
      </div>
      <div style={{ marginTop: 8, fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{valor}</div>
      <div style={{ marginTop: 6, color: 'var(--texto-suave)', fontSize: 13 }}>{detalhe}</div>
    </div>
  );
}

export default function Financeiro() {
  const hoje = new Date();
  const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const [dados, setDados] = useState<RespostaFinanceiro | null>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [de, setDe] = useState(iso(primeiroDiaMes));
  const [ate, setAte] = useState(iso(hoje));
  const [basePeriodo, setBasePeriodo] = useState('conclusao');
  const [status, setStatus] = useState('concluido');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [aba, setAba] = useState<'previa' | 'cobrancas'>('previa');
  const [msg, setMsg] = useState('');

  // Cobrança a partir de uma OS selecionada na prévia
  const [cobrarOs, setCobrarOs] = useState<LinhaFinanceiro | null>(null);
  const [cVencimento, setCVencimento] = useState('');
  const [cParcelas, setCParcelas] = useState('1');
  const [cDesconto, setCDesconto] = useState('');
  const [cAcrescimo, setCAcrescimo] = useState('');
  const [salvandoCobranca, setSalvandoCobranca] = useState(false);

  const carregar = useCallback(async () => {
    setErro('');
    setCarregando(true);
    try {
      const q = new URLSearchParams({
        de,
        ate,
        base_periodo: basePeriodo,
        status,
        pagina: String(pagina),
        limite: '100',
      });
      setDados(await api<RespostaFinanceiro>(`/financeiro/financeiro_os_cobrar.php?${q.toString()}`));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setCarregando(false);
    }
  }, [de, ate, basePeriodo, status, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    setPagina(1);
  }, [de, ate, basePeriodo, status]);

  async function criarCobrancaDaOs() {
    if (!cobrarOs) return;
    setErro('');
    if (!cVencimento) { setErro('Informe a data de vencimento da cobrança.'); return; }
    setSalvandoCobranca(true);
    try {
      await api('/financeiro/financeiro_documentos.php', {
        method: 'POST',
        body: {
          origem: 'os',
          ordem_servico_id: cobrarOs.id,
          vencimento: cVencimento,
          parcelas_qtd: Number(cParcelas) || 1,
          desconto: moedaParaNumero(cDesconto),
          acrescimo: moedaParaNumero(cAcrescimo),
        },
      });
      setMsg(`Cobrança da OS ${cobrarOs.codigo} criada como rascunho. Veja na aba Cobranças.`);
      setCobrarOs(null);
      setCVencimento(''); setCParcelas('1'); setCDesconto(''); setCAcrescimo('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvandoCobranca(false);
    }
  }

  const termo = normalizar(busca);
  const linhas = (dados?.linhas || []).filter((l) => {
    if (!termo) return true;
    return [l.codigo, l.cliente_nome, l.tecnico_nome, l.unidade_nome, l.tipo_os]
      .some((c) => c && normalizar(String(c)).includes(termo));
  });
  const podeVerMateriais = dados?.contexto.pode_ver_materiais !== false;
  const totalPaginas = dados?.paginacao.total_paginas ?? 1;

  return (
    <div>
      <div className="titulo-pg">
        <h1>Financeiro</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={aba === 'previa' ? 'btn sm' : 'btn sec sm'} onClick={() => setAba('previa')}>
            Prévia por OS
          </button>
          <button className={aba === 'cobrancas' ? 'btn sm' : 'btn sec sm'} onClick={() => setAba('cobrancas')}>
            Cobranças
          </button>
        </div>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      {aba === 'cobrancas' && <FinanceiroCobrancas onMensagem={setMsg} />}

      {aba === 'previa' && (
      <>
      <div className="grid cols4" style={{ marginBottom: 16 }}>
        {cardResumo('Total composto', `R$ ${fmtMoeda(dados?.totais.composto)}`, 'Materiais + mão de obra no período')}
        {cardResumo('Mão de obra', `R$ ${fmtMoeda(dados?.totais.mao_obra)}`, 'Tempo da OS × valor/hora do técnico')}
        {cardResumo(
          'Materiais',
          podeVerMateriais ? `R$ ${fmtMoeda(dados?.totais.materiais)}` : '—',
          podeVerMateriais ? 'Saídas de estoque lançadas nas OS' : 'Você não tem permissão para ver materiais',
        )}
        {cardResumo(
          'Orçamento (referência)',
          `R$ ${fmtMoeda(dados?.totais.orcamento_referencia)}`,
          'Valor de orçamento já digitado na OS — só comparação',
        )}
      </div>

      <div className="card" style={{ marginBottom: 16, display: 'grid', gap: 12 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12,
          }}
        >
          <label>
            De
            <input className="input" type="date" value={de} onChange={(e) => setDe(e.target.value)} />
          </label>
          <label>
            Até
            <input className="input" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
          </label>
          <label>
            Data de referência
            <select value={basePeriodo} onChange={(e) => setBasePeriodo(e.target.value)}>
              <option value="conclusao">Conclusão da OS</option>
              <option value="abertura">Abertura da OS</option>
            </select>
          </label>
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="concluido">Concluído</option>
              <option value="aguardando_aprovacao">Aguardando aprovação</option>
              <option value="em_andamento">Em andamento</option>
              <option value="interrompido">Interrompido</option>
              <option value="aberto">Aberto</option>
              <option value="cancelado">Cancelado</option>
              <option value="todos">Todos</option>
            </select>
          </label>
          <label>
            Buscar
            <input
              className="input"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="OS, cliente, técnico..."
            />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>
            Exibindo {linhas.length} de {dados?.paginacao.total_registros ?? 0} OS no filtro.
            {totalPaginas > 1 && ` Página ${dados?.paginacao.pagina} de ${totalPaginas}.`}
          </div>
          <button className="btn sec sm" onClick={carregar} disabled={carregando}>
            {carregando ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {cobrarOs && (
        <div className="card" style={{ marginBottom: 16, display: 'grid', gap: 12, borderLeft: '4px solid var(--azul)' }}>
          <div>
            <strong style={{ color: '#0f172a' }}>Cobrar a OS {cobrarOs.codigo}</strong>
            <div style={{ marginTop: 4, color: 'var(--texto-suave)', fontSize: 13 }}>
              Cliente: {cobrarOs.cliente_nome || cobrarOs.solicitante_nome || 'sem cliente vinculado'} · Valor composto:{' '}
              <strong>R$ {fmtMoeda(cobrarOs.total_composto)}</strong>
              {cobrarOs.mao_obra_incompleta && ' · atenção: mão de obra incompleta nesta OS'}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <label>
              1º vencimento
              <input className="input" type="date" value={cVencimento} onChange={(e) => setCVencimento(e.target.value)} />
            </label>
            <label>
              Parcelas
              <input className="input" inputMode="numeric" value={cParcelas}
                     onChange={(e) => setCParcelas(e.target.value.replace(/[^\d]/g, ''))} />
            </label>
            <label>
              Desconto (R$)
              <input className="input" inputMode="numeric" value={cDesconto} placeholder="0,00"
                     onChange={(e) => setCDesconto(mascaraMoedaBR(e.target.value))} />
            </label>
            <label>
              Acréscimo (R$)
              <input className="input" inputMode="numeric" value={cAcrescimo} placeholder="0,00"
                     onChange={(e) => setCAcrescimo(mascaraMoedaBR(e.target.value))} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" disabled={salvandoCobranca} onClick={criarCobrancaDaOs}>
              {salvandoCobranca ? 'Criando...' : 'Criar cobrança (rascunho)'}
            </button>
            <button className="btn sec" disabled={salvandoCobranca} onClick={() => setCobrarOs(null)}>Cancelar</button>
          </div>
        </div>
      )}

      <div className="tabela-wrap">
        <table>
          <thead>
            <tr>
              <th>OS</th>
              <th>Cliente</th>
              <th>Técnico</th>
              <th>Referência</th>
              <th>Tempo</th>
              <th>Mão de obra</th>
              {podeVerMateriais && <th>Materiais</th>}
              <th>Total composto</th>
              <th>Orçamento (ref.)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.id}>
                <td>
                  <Link to={`/app/os/${l.id}`}><strong>{l.codigo}</strong></Link>
                  <br />
                  <small style={{ color: 'var(--texto-suave)' }}>{l.tipo_os || '-'}</small>
                </td>
                <td>
                  {l.cliente_nome || l.solicitante_nome || '-'}
                  {!l.cliente_documento && l.cliente_nome && (
                    <>
                      <br />
                      <small style={{ color: '#b45309' }}>sem CPF/CNPJ</small>
                    </>
                  )}
                </td>
                <td>{l.tecnico_nome || '-'}</td>
                <td>{fmtData(l.referencia_periodo)}</td>
                <td>{fmtMinutos(l.tempo_total_minutos)}</td>
                <td>
                  R$ {fmtMoeda(l.mao_obra_valor)}
                  {l.mao_obra_incompleta && (
                    <>
                      {' '}
                      <span className="badge" style={{ background: '#fffbeb', color: '#b45309' }} title="Sem técnico ou sem valor/hora cadastrado">
                        incompleta
                      </span>
                    </>
                  )}
                </td>
                {podeVerMateriais && <td>R$ {fmtMoeda(l.materiais_total)}</td>}
                <td><strong>R$ {fmtMoeda(l.total_composto)}</strong></td>
                <td style={{ color: 'var(--texto-suave)' }}>
                  {l.orcamento_valor === null ? '-' : `R$ ${fmtMoeda(l.orcamento_valor)}`}
                </td>
                <td>
                  <button
                    className="btn sec sm"
                    disabled={l.total_composto <= 0}
                    title={l.total_composto <= 0 ? 'Sem valor para cobrar nesta OS' : 'Gerar cobrança desta OS'}
                    onClick={() => { setCobrarOs(l); setMsg(''); setErro(''); }}
                  >
                    Cobrar
                  </button>
                </td>
              </tr>
            ))}
            {!linhas.length && (
              <tr>
                <td colSpan={podeVerMateriais ? 10 : 9} style={{ textAlign: 'center', padding: 24 }}>
                  {carregando ? 'Carregando...' : 'Nenhuma OS encontrada nesse filtro.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPaginas > 1 && (
        <div className="card" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <div style={{ color: 'var(--texto-suave)', fontSize: 14 }}>
            Página {dados?.paginacao.pagina} de {totalPaginas}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn sec sm" disabled={pagina <= 1 || carregando} onClick={() => setPagina(1)}>Primeira</button>
            <button className="btn sec sm" disabled={pagina <= 1 || carregando} onClick={() => setPagina((p) => Math.max(1, p - 1))}>Anterior</button>
            <button className="btn sec sm" disabled={pagina >= totalPaginas || carregando} onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}>Próxima</button>
            <button className="btn sec sm" disabled={pagina >= totalPaginas || carregando} onClick={() => setPagina(totalPaginas)}>Última</button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
